import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BookOpen, Boxes, CalendarDays, FileSpreadsheet, History, Plus, Sparkles } from "lucide-react";
import {
  AppButton,
  AppCard,
  Callout,
  ConfirmModal,
  EmptyState,
  FieldGroup,
  FormActionBar,
  PageHeader,
  Radio,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import apiClient from "../../utils/apiClient";
import { usePlan } from "../../contexts/PlanContext";
import { aiGenerateSyllabus } from "../../utils/cmsAdminApi";
import { adminListSessions, getCourseSmartSettings, listTopicResources } from "../../utils/smartLearning";
import AiGenerateModal from "../Cms/AiGenerateModal";
import SyllabusImportModal from "../Courses/syllabusImport/SyllabusImportModal";
import { planClasses } from "./classPlanner";
import {
  blankDay,
  blankDayTopic,
  blankModule,
  blankTopic,
  emptySyllabus,
  formatMinutes,
  fromGroups,
  fromTemplate,
  isDirty,
  moveItem,
  syllabusStats,
  toPayload,
  validate,
  withSavedKeys,
} from "./syllabusModel";
import ModuleCard from "./ModuleCard";
import DayCard from "./DayCard";
import ClassPreview from "./ClassPreview";
import SmartLearningPanel from "./SmartLearningPanel";
import CodingPracticeCard from "./CodingPracticeCard";
import ModuleSessions from "./ModuleSessions";
import TopicResourcesDialog from "./TopicResourcesDialog";
import VersionsDialog from "./VersionsDialog";

const FORMATS = [
  {
    value: "module",
    label: "Modules and topics",
    description: "Give each topic its minutes; classes are formed from them at each batch’s class length.",
    Icon: Boxes,
  },
  {
    value: "day",
    label: "Day by day",
    description: "Write what each class day covers. Every day is one class.",
    Icon: CalendarDays,
  },
];

const dateOf = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : "");
const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

/** The groups worth opening when a syllabus loads: the first, or every one when there are few. */
const initiallyOpen = (state) => {
  const groups = state.format === "day" ? state.days : state.modules;
  return new Set(groups.length <= 3 ? groups.map((g) => g.uid) : groups.slice(0, 1).map((g) => g.uid));
};

/**
 * SyllabusBuilderPage — /courses/:id/syllabus.
 *
 * One place to write a course's syllabus and to see what a batch will get from
 * it: modules → topics (classes are formed from their minutes) or day by day,
 * with the class plan beside it, and the course's Smart Learning — formed from
 * the syllabus with resources on its topics, or authored session by session.
 *
 * Every save is a new version (nothing older changes). A batch already running
 * keeps the plan it started with; batches started afterwards use the new one.
 */
const SyllabusBuilderPage = () => {
  const { id: courseId } = useParams();
  const navigate = useNavigate();
  const { hasPlanFeature } = usePlan();
  const aiAvailable = hasPlanFeature("aiCmsContent");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [course, setCourse] = useState(null);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [versions, setVersions] = useState([]);
  const [links, setLinks] = useState(null);
  const [draft, setDraft] = useState(() => emptySyllabus());
  const [saved, setSaved] = useState(() => emptySyllabus());
  const [smartSettings, setSmartSettings] = useState(null);
  const [topicResources, setTopicResources] = useState([]);
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [openTopics, setOpenTopics] = useState(() => new Set());
  const [focusUid, setFocusUid] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [notice, setNotice] = useState(null); // { tone, text }
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);

  const smartEnabled = course?.features?.smartLearningFlow === true;
  const codingEnabled = hasPlanFeature("codingPractice") && course?.features?.codingPractice !== false;

  // ── Loading ────────────────────────────────────────────────────────────────
  const refreshSide = useCallback(() => {
    apiClient.get(`/syllabus/template/${courseId}/versions`).then((r) => setVersions(r.data?.templates || [])).catch(() => {});
    apiClient.get(`/syllabus/template/${courseId}/links`).then((r) => setLinks(r.data)).catch(() => {});
  }, [courseId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const [courseRes, templateRes] = await Promise.all([
          apiClient.get(`/courses/${courseId}`),
          apiClient.get(`/syllabus/template/${courseId}`)
            .catch((err) => (err?.response?.status === 404 ? null : Promise.reject(err))),
        ]);
        if (!alive) return;
        const template = templateRes?.data?.template || null;
        const state = template ? fromTemplate(template) : emptySyllabus("module");
        setCourse(courseRes.data.course);
        setActiveTemplate(template);
        setDraft(state);
        setSaved(state);
        setOpenGroups(initiallyOpen(state));
      } catch (err) {
        if (alive) setLoadError(err?.response?.data?.message || "The syllabus could not be loaded. Check your connection and try again.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    refreshSide();
    getCourseSmartSettings(courseId).then((s) => alive && setSmartSettings(s)).catch(() => {});
    return () => { alive = false; };
  }, [courseId, refreshSide]);

  const autoSessions = smartEnabled && smartSettings?.classSource === "auto";
  // Hand-written sessions are the default — also when the settings could not
  // be read, so a slow or failed read never hides a module's sessions.
  const authoredSessions = smartEnabled && !autoSessions;

  // How many sessions each module has, for the module headers — so a closed
  // module still says what is inside it.
  const [sessionCounts, setSessionCounts] = useState(() => new Map());
  useEffect(() => {
    if (!authoredSessions) return undefined;
    let alive = true;
    adminListSessions({ courseId })
      .then((data) => {
        if (!alive) return;
        const counts = new Map();
        for (const s of data.sessions || []) counts.set(s.moduleNumber, (counts.get(s.moduleNumber) || 0) + 1);
        setSessionCounts(counts);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [authoredSessions, courseId]);
  const setModuleSessionCount = useCallback(
    (moduleNumber, n) => setSessionCounts((prev) => (prev.get(moduleNumber) === n ? prev : new Map(prev).set(moduleNumber, n))),
    [],
  );
  const loadTopicResources = useCallback(async () => {
    try {
      setTopicResources(await listTopicResources(courseId));
    } catch { /* the buttons simply show no count */ }
  }, [courseId]);
  useEffect(() => { if (autoSessions) loadTopicResources(); }, [autoSessions, loadTopicResources]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const dirty = useMemo(() => isDirty(draft, saved), [draft, saved]);
  const validation = useMemo(() => validate(draft), [draft]);
  const errors = showErrors ? validation.byUid : {};
  const stats = useMemo(() => syllabusStats(draft), [draft]);
  const classes = useMemo(
    () => (draft.format === "module" ? planClasses({ modules: draft.modules, classMinutes: draft.classMinutes }) : []),
    [draft.format, draft.modules, draft.classMinutes],
  );
  const classesByModule = useMemo(() => {
    const out = new Map();
    for (const c of classes) out.set(c.moduleIndex, (out.get(c.moduleIndex) || 0) + 1);
    return out;
  }, [classes]);
  const resourcesByKey = useMemo(() => {
    const out = new Map();
    for (const r of topicResources) {
      if (!out.has(r.topicKey)) out.set(r.topicKey, []);
      out.get(r.topicKey).push(r);
    }
    return out;
  }, [topicResources]);

  const savedIsModule = activeTemplate?.syllabusType === "module" && (activeTemplate.modules || []).length > 0;
  let smartBlocked = "";
  if (draft.format !== "module") smartBlocked = "Smart Learning works from a module-wise syllabus. Choose “Modules and topics”, then save.";
  else if (!savedIsModule) smartBlocked = "Save the module-wise syllabus first, then switch Smart Learning on.";
  else if (dirty) smartBlocked = "Save your syllabus changes first, then switch Smart Learning on.";

  // ── Leaving with unsaved changes ──────────────────────────────────────────
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // ── Editing ────────────────────────────────────────────────────────────────
  const toggleIn = (setter) => (id) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleGroup = toggleIn(setOpenGroups);
  const toggleTopic = toggleIn(setOpenTopics);
  const openGroup = (id) => setOpenGroups((prev) => new Set(prev).add(id));

  const setModules = (fn) => setDraft((d) => ({ ...d, modules: fn(d.modules) }));
  const setDays = (fn) => setDraft((d) => ({ ...d, days: fn(d.days) }));
  const mapModule = (muid, fn) => setModules((ms) => ms.map((m) => (m.uid === muid ? fn(m) : m)));
  const mapDay = (duid, fn) => setDays((ds) => ds.map((d) => (d.uid === duid ? fn(d) : d)));

  const addModule = () => {
    const m = blankModule();
    setModules((ms) => [...ms, m]);
    openGroup(m.uid);
    setFocusUid(m.uid);
  };
  const addTopicAfter = (muid, afterUid) => {
    const t = blankTopic();
    mapModule(muid, (m) => {
      const at = afterUid ? m.topics.findIndex((x) => x.uid === afterUid) + 1 : m.topics.length;
      const topics = [...m.topics];
      topics.splice(at > 0 ? at : topics.length, 0, t);
      return { ...m, topics };
    });
    setFocusUid(t.uid);
  };
  const addDay = () => {
    const d = blankDay();
    setDays((ds) => [...ds, d]);
    openGroup(d.uid);
    setFocusUid(d.uid);
  };
  const addDayTopicAfter = (duid, afterUid) => {
    const t = blankDayTopic();
    mapDay(duid, (d) => {
      const at = afterUid ? d.topics.findIndex((x) => x.uid === afterUid) + 1 : d.topics.length;
      const topics = [...d.topics];
      topics.splice(at > 0 ? at : topics.length, 0, t);
      return { ...d, topics };
    });
    setFocusUid(t.uid);
  };

  const renameClass = (topicUid, title) => setModules((ms) => ms.map((m) => ({
    ...m,
    topics: m.topics.map((t) => (t.uid === topicUid
      ? { ...t, classTitle: title, startsNewClass: title ? true : t.startsNewClass }
      : t)),
  })));

  const chooseFormat = (next) => {
    if (next === draft.format) return;
    const current = draft.format === "day" ? draft.days : draft.modules;
    if (current.length) {
      setDialog({ type: "format", next });
      return;
    }
    setDraft((d) => ({ ...d, format: next }));
  };

  // ── Saving ─────────────────────────────────────────────────────────────────
  const save = async () => {
    if (validation.list.length) {
      setShowErrors(true);
      const n = validation.list.length;
      setNotice({ tone: "danger", text: `${plural(n, "thing")} to fix before saving. First: ${validation.list[0].message}` });
      const groups = draft.format === "day" ? draft.days : draft.modules;
      const holding = groups.filter((g) => validation.list.some((e) => e.uid.startsWith(g.uid) || (g.topics || []).some((t) => t.uid === e.uid)));
      setOpenGroups((prev) => new Set([...prev, ...holding.map((g) => g.uid)]));
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      const { data } = await apiClient.post("/syllabus/template", toPayload(draft, courseId));
      const next = withSavedKeys(draft, data.template);
      setDraft(next);
      setSaved(next);
      setActiveTemplate(data.template);
      setShowErrors(false);
      const running = links?.startedBatches || 0;
      setNotice({
        tone: "success",
        text: `Saved as version ${data.template.version}.${running ? ` ${plural(running, "batch", "batches")} already running keep the plan they started with; batches started from now use this version.` : ""}`,
      });
      refreshSide();
      if (autoSessions) loadTopicResources();
    } catch (err) {
      setNotice({ tone: "danger", text: err?.response?.data?.message || "The syllabus was not saved. Check your connection and try again." });
    } finally {
      setSaving(false);
    }
  };

  const applyGroups = (format, groups, message) => {
    const next = fromGroups(format, groups, draft);
    setDraft(next);
    setOpenGroups(initiallyOpen(next));
    setShowErrors(false);
    setNotice({ tone: "info", text: message });
  };

  // ── Rendering ──────────────────────────────────────────────────────────────
  const pageHeader = (
    <PageHeader
      icon={BookOpen}
      title="Syllabus"
      description={course?.name ? `What ${course.name} teaches, and how a batch is planned from it` : undefined}
      backButton
      onBack={() => (dirty ? setDialog({ type: "leave" }) : navigate(`/courses/${courseId}`))}
    />
  );

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {pageHeader}
        <Shimmer height={88} />
        <div className="tw-grid tw-gap-4 min-[1200px]:tw-grid-cols-[minmax(0,1fr)_360px]">
          <Shimmer height={180} count={3} />
          <Shimmer height={320} />
        </div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {pageHeader}
        <Callout tone="danger" role="alert">{loadError}</Callout>
      </div>
    );
  }

  const isModule = draft.format === "module";
  const groups = isModule ? draft.modules : draft.days;
  const classCount = isModule ? classes.length : draft.days.length;
  const allOpen = groups.length > 0 && groups.every((g) => openGroups.has(g.uid));

  const topicResourcesFor = (moduleState) => (topic) => {
    if (!(autoSessions && isModule)) return null;
    if (!topic.key) return { disabledReason: "Save to attach resources" };
    return {
      count: (resourcesByKey.get(topic.key) || []).length,
      onOpen: () => setDialog({ type: "resources", topic, moduleTitle: moduleState.title }),
    };
  };

  const emptyChoices = (
    <AppCard>
      <EmptyState
        icon={isModule ? Boxes : CalendarDays}
        title={isModule ? "Start with the first module" : "Start with the first day"}
        description={isModule
          ? "Write the modules and their topics with how long each takes. Or bring in a spreadsheet, or let AI draft one to edit."
          : "Write what each class day covers. Or bring in a spreadsheet, or let AI draft one to edit."}
        action={(
          <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
            <AppButton onClick={isModule ? addModule : addDay}>
              <Plus size={15} aria-hidden="true" /> {isModule ? "Add a module" : "Add a day"}
            </AppButton>
            <AppButton variant="outline" onClick={() => setDialog({ type: "import" })}>
              <FileSpreadsheet size={15} aria-hidden="true" /> Import a spreadsheet
            </AppButton>
            {aiAvailable && (
              <AppButton variant="outline" onClick={() => setDialog({ type: "ai" })}>
                <Sparkles size={15} aria-hidden="true" /> Draft with AI
              </AppButton>
            )}
          </div>
        )}
      />
    </AppCard>
  );

  return (
    <div className="has-form-actionbar tw-flex tw-flex-col tw-gap-4">
      {pageHeader}

      {/* ── Summary ── */}
      <AppCard as="section" aria-label="Syllabus summary">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-3 tw-p-4">
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
            <h2 className="tw-m-0 tw-truncate tw-text-lg tw-font-semibold tw-text-content">{course?.name || "Course"}</h2>
            <p className="tw-m-0 tw-text-xs tw-text-content-muted">
              {activeTemplate ? `Version ${activeTemplate.version} in use · saved ${dateOf(activeTemplate.createdAt)}` : "Nothing saved yet"}
              {dirty && <span className="tw-font-semibold tw-text-warning-fg"> · unsaved changes</span>}
            </p>
          </div>
          <dl className="tw-m-0 tw-flex tw-flex-wrap tw-gap-x-5 tw-gap-y-1 tw-text-sm">
            {[
              [isModule ? "Modules" : "Days", stats.groups],
              ["Topics", stats.topics],
              ["Teaching", formatMinutes(stats.minutes)],
              ["Classes", classCount],
            ].map(([label, value]) => (
              <div key={label} className="tw-flex tw-items-baseline tw-gap-1.5">
                <dt className="tw-text-xs tw-text-content-muted">{label}</dt>
                <dd className="tw-m-0 tw-font-semibold tw-text-content">{value}</dd>
              </div>
            ))}
          </dl>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            <AppButton size="sm" variant="outline" onClick={() => setDialog({ type: "import" })}>
              <FileSpreadsheet size={14} aria-hidden="true" /> Import
            </AppButton>
            {aiAvailable && (
              <AppButton size="sm" variant="outline" onClick={() => setDialog({ type: "ai" })}>
                <Sparkles size={14} aria-hidden="true" /> Draft with AI
              </AppButton>
            )}
            <AppButton size="sm" variant="outline" onClick={() => setDialog({ type: "versions" })} disabled={!versions.length}>
              <History size={14} aria-hidden="true" /> Versions{versions.length ? ` · ${versions.length}` : ""}
            </AppButton>
          </div>
        </div>
      </AppCard>

      {notice && (
        <Callout tone={notice.tone} role={notice.tone === "danger" ? "alert" : "status"}>{notice.text}</Callout>
      )}
      {dirty && (links?.startedBatches || 0) > 0 && (
        <Callout tone="info">
          {plural(links.startedBatches, "batch", "batches")} already started from this syllabus. They keep their classes;
          batches started after you save use the new version.
        </Callout>
      )}

      <div className="tw-grid tw-items-start tw-gap-4 min-[1200px]:tw-grid-cols-[minmax(0,1fr)_minmax(320px,380px)]">
        {/* ── The syllabus ── */}
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4">
          <FieldGroup legend="How is this course written?">
            <div className="tw-grid tw-gap-2 min-[768px]:tw-grid-cols-2">
              {FORMATS.map(({ value, label, description, Icon }) => (
                <Radio
                  key={value}
                  variant="card"
                  name="sb-format"
                  value={value}
                  checked={draft.format === value}
                  onChange={() => chooseFormat(value)}
                  labelClassName="tw-font-semibold"
                  label={(
                    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                      <Icon size={15} aria-hidden="true" className="tw-text-primary" /> {label}
                    </span>
                  )}
                  description={description}
                />
              ))}
            </div>
          </FieldGroup>

          {errors.syllabus && <Callout tone="danger" role="alert">{errors.syllabus}</Callout>}

          {groups.length === 0 ? emptyChoices : (
            <>
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                <h2 className="tw-m-0 tw-text-[0.9375rem] tw-font-semibold tw-text-content">
                  {isModule ? plural(groups.length, "module") : plural(groups.length, "day")}
                </h2>
                <AppButton
                  size="xs"
                  variant="ghost"
                  onClick={() => setOpenGroups(allOpen ? new Set() : new Set(groups.map((g) => g.uid)))}
                >
                  {allOpen ? "Collapse all" : "Expand all"}
                </AppButton>
              </div>

              {isModule && draft.modules.map((m, i) => (
                <ModuleCard
                  key={m.uid}
                  module={m}
                  index={i}
                  count={draft.modules.length}
                  open={openGroups.has(m.uid)}
                  onToggle={() => toggleGroup(m.uid)}
                  classCount={classesByModule.get(i) || 0}
                  sessionCount={authoredSessions ? sessionCounts.get(i + 1) || 0 : null}
                  errors={errors}
                  openTopics={openTopics}
                  onToggleTopic={toggleTopic}
                  focusUid={focusUid}
                  onChange={(patch) => mapModule(m.uid, (x) => ({ ...x, ...patch }))}
                  onMove={(delta) => setModules((ms) => moveItem(ms, i, delta))}
                  onRemove={() => setDialog({ type: "removeModule", uid: m.uid, name: m.title.trim() || `Module ${i + 1}` })}
                  onTopicChange={(tuid, patch) => mapModule(m.uid, (x) => ({ ...x, topics: x.topics.map((t) => (t.uid === tuid ? { ...t, ...patch } : t)) }))}
                  onTopicAddAfter={(tuid) => addTopicAfter(m.uid, tuid)}
                  onTopicMove={(tuid, delta) => mapModule(m.uid, (x) => ({ ...x, topics: moveItem(x.topics, x.topics.findIndex((t) => t.uid === tuid), delta) }))}
                  onTopicRemove={(tuid) => mapModule(m.uid, (x) => ({ ...x, topics: x.topics.filter((t) => t.uid !== tuid) }))}
                  topicResources={topicResourcesFor(m)}
                >
                  {authoredSessions && (
                    !dirty && savedIsModule ? (
                      <ModuleSessions
                        courseId={courseId}
                        moduleNumber={i + 1}
                        moduleTitle={m.title}
                        codingEnabled={codingEnabled}
                        onCountChange={(n) => setModuleSessionCount(i + 1, n)}
                      />
                    ) : (
                      <p className="tw-m-0 tw-rounded-md tw-border tw-border-dashed tw-border-line tw-px-3 tw-py-2 tw-text-xs tw-text-content-muted">
                        Save the syllabus to manage this module’s sessions.
                      </p>
                    )
                  )}
                </ModuleCard>
              ))}

              {!isModule && draft.days.map((d, i) => (
                <DayCard
                  key={d.uid}
                  day={d}
                  index={i}
                  count={draft.days.length}
                  open={openGroups.has(d.uid)}
                  onToggle={() => toggleGroup(d.uid)}
                  errors={errors}
                  focusUid={focusUid}
                  onChange={(patch) => mapDay(d.uid, (x) => ({ ...x, ...patch }))}
                  onMove={(delta) => setDays((ds) => moveItem(ds, i, delta))}
                  onRemove={() => setDays((ds) => ds.filter((x) => x.uid !== d.uid))}
                  onDuplicate={() => {
                    const copy = { ...blankDay(d.title), minutes: d.minutes, topics: d.topics.map((t) => blankDayTopic(t.title)) };
                    setDays((ds) => [...ds.slice(0, i + 1), copy, ...ds.slice(i + 1)]);
                    openGroup(copy.uid);
                  }}
                  onTopicChange={(tuid, title) => mapDay(d.uid, (x) => ({ ...x, topics: x.topics.map((t) => (t.uid === tuid ? { ...t, title } : t)) }))}
                  onTopicAddAfter={(tuid) => addDayTopicAfter(d.uid, tuid)}
                  onTopicRemove={(tuid) => mapDay(d.uid, (x) => ({ ...x, topics: x.topics.filter((t) => t.uid !== tuid) }))}
                />
              ))}

              <div>
                <AppButton variant="outline" onClick={isModule ? addModule : addDay}>
                  <Plus size={15} aria-hidden="true" /> {isModule ? "Add a module" : "Add a day"}
                </AppButton>
              </div>
            </>
          )}
        </div>

        {/* ── What a batch gets, and the course's settings ── */}
        <aside className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4 min-[1200px]:tw-sticky min-[1200px]:tw-top-4">
          {isModule ? (
            <ClassPreview
              classes={classes}
              classMinutes={draft.classMinutes}
              onClassMinutes={(n) => setDraft((d) => ({ ...d, classMinutes: n }))}
              onRename={renameClass}
            />
          ) : (
            <AppCard as="section" aria-label="Classes">
              <div className="tw-flex tw-flex-col tw-gap-1 tw-p-4">
                <h2 className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-[0.9375rem] tw-font-semibold tw-text-content">
                  <CalendarDays size={16} aria-hidden="true" className="tw-text-primary" /> Classes
                </h2>
                <p className="tw-m-0 tw-text-sm tw-text-content">
                  {plural(draft.days.length, "class", "classes")}
                  <span className="tw-text-content-muted"> · one per day, on each batch’s class days</span>
                </p>
              </div>
            </AppCard>
          )}
          <SmartLearningPanel
            courseId={courseId}
            enabled={smartEnabled}
            onEnabledChange={(on) => setCourse((c) => ({ ...c, features: { ...(c?.features || {}), smartLearningFlow: on } }))}
            blockedReason={smartBlocked}
            settings={smartSettings}
            onSettings={setSmartSettings}
          />
          <CodingPracticeCard
            courseId={courseId}
            value={course?.features?.codingPractice}
            onChange={(v) => setCourse((c) => ({ ...c, features: { ...(c?.features || {}), codingPractice: v } }))}
          />
        </aside>
      </div>

      {groups.length > 0 && (
        <FormActionBar
          info={dirty
            ? <span className="tw-text-[0.8125rem] tw-font-medium tw-text-warning-fg">Unsaved changes</span>
            : <span className="tw-text-[0.8125rem] tw-text-content-muted">All changes saved</span>}
        >
          <AppButton variant="outline" onClick={() => setDialog({ type: "discard" })} disabled={!dirty || saving}>Discard</AppButton>
          <AppButton onClick={save} disabled={!dirty || saving}>{saving ? "Saving…" : "Save syllabus"}</AppButton>
        </FormActionBar>
      )}

      {/* ── Dialogs ── */}
      <ConfirmModal
        show={dialog?.type === "format"}
        onHide={() => setDialog(null)}
        onConfirm={() => { setDraft((d) => ({ ...d, format: dialog.next })); setDialog(null); }}
        title={dialog?.next === "day" ? "Write it day by day?" : "Write it as modules and topics?"}
        message={`Your ${draft.format === "day" ? "days" : "modules"} stay here if you switch back. Only the way you save is kept in the new version — the earlier version stays under Versions.`}
        confirmText="Switch"
        cancelText="Stay"
        variant="warning"
      />
      <ConfirmModal
        show={dialog?.type === "removeModule"}
        onHide={() => setDialog(null)}
        onConfirm={() => { setModules((ms) => ms.filter((m) => m.uid !== dialog.uid)); setDialog(null); }}
        title="Delete this module?"
        message={`“${dialog?.name || ""}” and its topics leave the syllabus when you save. Batches already running are not changed.`}
        confirmText="Delete module"
        variant="danger"
      />
      <ConfirmModal
        show={dialog?.type === "discard"}
        onHide={() => setDialog(null)}
        onConfirm={() => { setDraft(saved); setShowErrors(false); setNotice(null); setDialog(null); }}
        title="Discard your changes?"
        message="The syllabus goes back to the version in use."
        confirmText="Discard changes"
        variant="danger"
      />
      <ConfirmModal
        show={dialog?.type === "leave"}
        onHide={() => setDialog(null)}
        onConfirm={() => navigate(`/courses/${courseId}`)}
        title="Leave without saving?"
        message="Your changes to the syllabus are not saved."
        confirmText="Leave"
        cancelText="Stay"
        variant="warning"
      />
      <VersionsDialog
        show={dialog?.type === "versions"}
        onHide={() => setDialog(null)}
        versions={versions}
        onLoad={(v) => {
          const next = fromTemplate(v);
          setDraft(next);
          setOpenGroups(initiallyOpen(next));
          setDialog(null);
          setNotice({ tone: "info", text: `Version ${v.version} is in the editor. Save to make it the newest version.` });
        }}
      />
      <SyllabusImportModal
        show={dialog?.type === "import"}
        onHide={() => setDialog(null)}
        courseId={courseId}
        currentFormat={draft.format}
        current={{
          days: toPayload({ ...draft, format: "day" }, courseId).days,
          modules: toPayload({ ...draft, format: "module" }, courseId).modules,
        }}
        savedTemplate={activeTemplate}
        onApply={({ format, groups: imported, importedCount, mode }) => {
          setDialog(null);
          applyGroups(format, imported,
            `${mode === "append" ? "Added" : "Imported"} ${plural(importedCount, format === "day" ? "day" : "module")}. Review it, then save.`);
        }}
      />
      {aiAvailable && (
        <SyllabusAiDialog
          show={dialog?.type === "ai"}
          onHide={() => setDialog(null)}
          course={course}
          courseId={courseId}
          format={draft.format}
          onApply={(format, generated) => {
            setDialog(null);
            applyGroups(format, generated, `AI drafted ${plural(generated.length, format === "day" ? "day" : "module")}. Review it, then save.`);
          }}
        />
      )}
      <TopicResourcesDialog
        show={dialog?.type === "resources"}
        onHide={() => setDialog(null)}
        courseId={courseId}
        topic={dialog?.type === "resources" ? dialog.topic : null}
        moduleTitle={dialog?.moduleTitle}
        resources={dialog?.type === "resources" ? resourcesByKey.get(dialog.topic.key) || [] : []}
        codingEnabled={codingEnabled}
        onChanged={loadTopicResources}
      />
    </div>
  );
};

/**
 * The AI draft, in the editor's current format. Its result loads into the
 * editor unsaved, like an import.
 */
function SyllabusAiDialog({ show, onHide, course, courseId, format, onApply }) {
  const [count, setCount] = useState(format === "day" ? 30 : 8);
  const [level, setLevel] = useState("beginner");
  const [focus, setFocus] = useState("");
  const isDay = format === "day";

  useEffect(() => { setCount(isDay ? 30 : 8); }, [isDay]);

  return (
    <AiGenerateModal
      show={show}
      onHide={onHide}
      title={isDay ? "Draft a day-by-day syllabus" : "Draft modules and topics"}
      description="The AI drafts it; you review and edit it here before anything is saved."
      extraFields={[
        isDay
          ? { key: "totalDays", label: "Days", type: "number", required: true, min: 1, max: 180, value: count, onChange: (v) => setCount(Number(v) || 0), hint: "How many class days (1–180)." }
          : { key: "totalModules", label: "Modules", type: "number", required: true, min: 1, max: 30, value: count, onChange: (v) => setCount(Number(v) || 0), hint: "How many modules (1–30)." },
        { key: "level", label: "Level", type: "text", value: level, onChange: setLevel, placeholder: "beginner / intermediate / advanced" },
        { key: "focus", label: "Focus (optional)", type: "text", value: focus, onChange: setFocus, placeholder: "e.g. React, Node.js, MongoDB" },
      ]}
      onGenerate={async ({ prompt, tone, ...ai }, { regenerate }) => {
        if ((Number(count) || 0) < 1) throw new Error(isDay ? "Ask for at least one day." : "Ask for at least one module.");
        return aiGenerateSyllabus({
          courseId,
          syllabusType: format,
          courseName: course?.name || "this course",
          ...(isDay ? { totalDays: Number(count) } : { totalModules: Number(count) }),
          level: level || "beginner",
          focusTopics: focus ? focus.split(",").map((t) => t.trim()).filter(Boolean) : [],
          prompt,
          tone,
          includeProjects: true,
          regenerate,
          ...ai,
        });
      }}
      renderPreview={(data) => {
        const items = (isDay ? data?.days : data?.modules) || [];
        if (!items.length) return <p className="tw-m-0 tw-text-sm tw-text-content-muted">Nothing came back — try again.</p>;
        return (
          <div className="tw-flex tw-flex-col tw-gap-2">
            <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">
              {plural(items.length, isDay ? "day" : "module")} drafted
            </p>
            <ol className="tw-m-0 tw-flex tw-max-h-80 tw-flex-col tw-gap-1.5 tw-overflow-y-auto tw-ps-5 tw-text-[0.8125rem] tw-text-content">
              {items.map((g, i) => (
                <li key={i}>
                  <span className="tw-font-semibold">{g.title}</span>
                  {Array.isArray(g.topics) && g.topics.length > 0 && (
                    <span className="tw-text-content-muted">
                      {" — "}{g.topics.slice(0, 5).map((t) => (typeof t === "string" ? t : t.title)).join(", ")}
                      {g.topics.length > 5 ? `, +${g.topics.length - 5} more` : ""}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        );
      }}
      onApply={(data) => {
        const items = (isDay ? data?.days : data?.modules) || [];
        if (items.length) onApply(format, items);
      }}
    />
  );
}

export default SyllabusBuilderPage;
